/**
 * Single source of truth for allocation/lifecycle status coloring in the
 * launch area, backed by the scoped `--launch-status-*` / `--launch-lifecycle-*`
 * tokens (src/features/launches/styles/launch-tokens.css). Allocation status
 * never borrows from the --accent-* chart palette — that palette is reserved
 * for category/broker differentiation (donut segments aside, which use their
 * own solid tone here; broker ranking bars keep --accent-*).
 */
import type { AllocationStatus, LaunchStatus } from '../types';

/** Solid status color per allocation status — donut segments, metric numbers, activity dots. */
export const ALLOCATION_STATUS_COLOR: Record<AllocationStatus, string> = {
  deposit_pending: 'var(--launch-status-deposit-solid)',
  available: 'var(--launch-status-available-solid)',
  held: 'var(--launch-status-held-solid)',
  reserved_with_deposit: 'var(--launch-status-reserved-solid)',
  sold: 'var(--launch-status-sold-solid)',
};

/** Legend / badge classes using the scoped launch status tokens. */
export const ALLOCATION_STATUS_BADGE: Record<AllocationStatus, string> = {
  deposit_pending: 'bg-[var(--launch-status-deposit-pill-bg)] text-[var(--launch-status-deposit-pill-fg)]',
  available: 'bg-[var(--launch-status-available-pill-bg)] text-[var(--launch-status-available-pill-fg)]',
  held: 'bg-[var(--launch-status-held-pill-bg)] text-[var(--launch-status-held-pill-fg)]',
  reserved_with_deposit: 'bg-[var(--launch-status-reserved-pill-bg)] text-[var(--launch-status-reserved-pill-fg)]',
  sold: 'bg-[var(--launch-status-sold-pill-bg)] text-[var(--launch-status-sold-pill-fg)]',
};

/** Lifecycle badge classes for draft / open / closed launches. */
export const LAUNCH_LIFECYCLE_BADGE: Record<LaunchStatus, string> = {
  draft: 'bg-[var(--launch-lifecycle-draft-bg)] text-[var(--launch-lifecycle-draft-fg)]',
  open: 'bg-[var(--launch-lifecycle-open-bg)] text-[var(--launch-lifecycle-open-fg)]',
  closed: 'bg-[var(--launch-lifecycle-closed-bg)] text-[var(--launch-lifecycle-closed-fg)]',
};

/** Legend dot fill per allocation status. */
export const ALLOCATION_STATUS_DOT: Record<AllocationStatus, string> = {
  deposit_pending: 'bg-[var(--launch-status-deposit-solid)]',
  available: 'bg-[var(--launch-status-available-solid)]',
  held: 'bg-[var(--launch-status-held-solid)]',
  reserved_with_deposit: 'bg-[var(--launch-status-reserved-solid)]',
  sold: 'bg-[var(--launch-status-sold-solid)]',
};

/** Card surface / left accent classes per allocation status. */
export const ALLOCATION_STATUS_CARD: Record<AllocationStatus, string> = {
  deposit_pending: 'border-l-[3px] border-l-[var(--launch-status-deposit-border)] bg-[var(--launch-status-deposit-bg)]',
  available: 'border-l-[3px] border-l-[var(--launch-status-available-border)] bg-[var(--launch-status-available-bg)]',
  held: 'border-l-[3px] border-l-[var(--launch-status-held-border)] bg-[var(--launch-status-held-bg)]',
  reserved_with_deposit: 'border-l-[3px] border-l-[var(--launch-status-reserved-border)] bg-[var(--launch-status-reserved-bg)]',
  sold: 'border-l-[3px] border-l-[var(--launch-status-sold-border)] bg-[var(--launch-status-sold-bg)]',
};

/** Tile fill / border / text classes per allocation status. */
export const ALLOCATION_STATUS_TILE: Record<AllocationStatus, string> = {
  deposit_pending: 'bg-[var(--launch-status-deposit-bg)] border-[var(--launch-status-deposit-border)] text-[var(--launch-status-deposit-fg)]',
  available: 'bg-[var(--launch-status-available-bg)] border-[var(--launch-status-available-border)] text-[var(--launch-status-available-fg)]',
  held: 'bg-[var(--launch-status-held-bg)] border-[var(--launch-status-held-border)] text-[var(--launch-status-held-fg)]',
  reserved_with_deposit: 'bg-[var(--launch-status-reserved-bg)] border-[var(--launch-status-reserved-border)] text-[var(--launch-status-reserved-fg)]',
  sold: 'bg-[var(--launch-status-sold-bg)] border-[var(--launch-status-sold-border)] text-[var(--launch-status-sold-fg)]',
};

/** Solid color per allocation action (activity feed timeline dots). */
export const ALLOCATION_ACTION_COLOR: Record<string, string> = {
  hold: 'var(--launch-status-held-solid)',
  self_release: 'var(--text-tertiary)',
  confirm_deposit: 'var(--launch-status-reserved-solid)',
  mark_sold: 'var(--launch-status-sold-solid)',
  force_release: 'var(--launch-danger-fg)',
  revert_sold: 'var(--seed-base)',
  submit_deposit: 'var(--launch-status-deposit-solid)',
  validate_deposit: 'var(--launch-status-reserved-solid)',
  reject_deposit: 'var(--launch-danger-fg)',
  expire_deposit: 'var(--text-secondary)',
  expired: 'var(--text-secondary)',
};

/**
 * Seconds under which a hold countdown reads as urgent.
 *
 * Three minutes is the point where a broker still has time to act but not
 * to wander off, which is the whole reason the number changes colour.
 */
export const URGENT_COUNTDOWN_SECONDS = 180;

/**
 * Colour token for a hold countdown at *seconds* remaining.
 *
 * A function rather than an inline ternary in two components: the
 * threshold is a product rule, and it is the part worth a test — waiting
 * twelve minutes in a browser to watch a number turn red is not one.
 *
 * @param seconds - Seconds remaining, or null when there is no countdown.
 * @returns The CSS variable to colour the countdown with.
 */
export function countdownColor(seconds: number | null | undefined): string {
  if (seconds == null) return 'var(--text-tertiary)';
  return seconds < URGENT_COUNTDOWN_SECONDS
    ? 'var(--launch-danger-fg)'
    : 'var(--launch-status-held-fg)';
}
